import { Button } from "components/DesignSystem/Button/Button";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useBulkDeleteProductsMutation } from "store/apis/productAndServices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { BulkDeleteProductsModal } from "./BulkDeleteProductsModal";

type ProductBulkActionsProps = {
  selectedProductIds: string[];
  onClearSelection: () => void;
  groupId: string;
  entityId: string;
};

export const ProductBulkActions = ({
  selectedProductIds,
  onClearSelection,
  groupId,
  entityId,
}: ProductBulkActionsProps) => {
  const deleteModal = useModal();
  const { successToast, warning, alertToast } = useToast();
  const [bulkDeleteProducts, { isLoading }] = useBulkDeleteProductsMutation();

  const selectedCount = selectedProductIds.length;

  const showResultToast = (deletedCount: number, skippedCount: number) => {
    if (skippedCount === 0) {
      successToast({
        message: `${pluralize(deletedCount, "item", "items")} deleted`,
      });
      return;
    }

    if (deletedCount > 0) {
      warning({
        message: `${deletedCount} deleted, ${skippedCount} skipped (Stripe products can't be deleted)`,
      });
      return;
    }

    alertToast({ message: "Stripe products can't be deleted" });
  };

  const handleDelete = async () => {
    try {
      const { deleted_product_ids, skipped_product_ids } =
        await bulkDeleteProducts({
          groupId,
          entityId,
          productIds: selectedProductIds,
        }).unwrap();

      showResultToast(deleted_product_ids.length, skipped_product_ids.length);
      deleteModal.close();
      onClearSelection();
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      <SelectedFloatingControl>
        <>
          <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
            {selectedCount} selected
          </span>
          <span className="t-text-neutral-10 t-h-full">
            <DividerLine />
          </span>
          <Button size="small" customType="danger" onClick={deleteModal.open}>
            Delete
          </Button>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={onClearSelection}
            aria-label="Clear selection"
          >
            <Cross />
          </Button>
        </>
      </SelectedFloatingControl>

      <BulkDeleteProductsModal
        open={deleteModal.isOpen}
        count={selectedCount}
        isLoading={isLoading}
        onCancel={deleteModal.close}
        onConfirm={handleDelete}
      />
    </>
  );
};
